import React, {
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useRef
} from "react";
import * as h3 from "h3-js";
import {useTranslation} from "react-i18next";
import {ShopOutlined} from "@ant-design/icons";
import {message} from "antd";
import L from "leaflet";

import {pickupColor} from "../../constants/constants";
import {resolutionOptions} from "../../constants/constants";
import {useH3Operations} from "../../hooks/useH3Operations";
import {MapContainer, Marker, GlobalStyle} from "./H3Map.styles";
import {H3MapTools} from "./components/H3MapTools";
import {Hexagon} from "./components/Hexagon";
import {OtherHexagonsPanel} from "./components/OtherHexagonsPanel";
import {TileLayerControl} from "../_defaultComponents/TileLayerControl/TileLayerControl";
import {H3MapConfig} from "./config";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../utils/helpers";

export const H3Map = forwardRef(
    (
        {
            markers = [],
            markerIcon = <ShopOutlined />,
            markerColor = pickupColor,
            initialHexagons = [],
            otherHexagons = [],
            onHexagonSelect,
            defaultCenter = H3MapConfig.map.defaultCenter,
            defaultZoom = H3MapConfig.map.defaultZoom,
            onMarkerClick,
            isH3Enabled = H3MapConfig.features.isH3Enabled,
            style = {},
            initialCity = null,
            disabledResolutions = [],
            isPreview = H3MapConfig.features.isPreview,
            showOtherHexagonsPanel = H3MapConfig.features.showOtherHexagonsPanel,
            autoResolution = H3MapConfig.features.autoResolution,
            showToolsPanel = H3MapConfig.features.showToolsPanel,
            isActionsEnabled = H3MapConfig.features.isActionsEnabled,
            onlyH3Resolutions = H3MapConfig.features.onlyH3Resolutions,
            isToolsPanelVisibleOnInitial = H3MapConfig.features.isToolsPanelVisibleOnInitial,
            showHexagonTooltip = H3MapConfig.features.showHexagonTooltip,
            showTileLayerControl = false
        },
        ref
    ) => {
        const {i18n} = useTranslation([]);
        const mapRef = useRef(null);
        const [mapInstance, setMapInstance] = useState(null);
        const tileLayerRef = useRef(null);
        const [isToolsPanelVisible, setIsToolsPanelVisible] = useState(
            isToolsPanelVisibleOnInitial
        );
        const [showOtherHexagons, setShowOtherHexagons] = useState(
            H3MapConfig.features.showOtherHexagons
        );
        const [filteredOtherHexagons, setFilteredOtherHexagons] = useState(otherHexagons);
        const [hasAutoFitted, setHasAutoFitted] = useState(false);
        const [markerPositions, setMarkerPositions] = useState({});
        const [hoveredMarker, setHoveredMarker] = useState(null);

        const {
            resolution,
            setResolution,
            selectedHexagons,
            markerToHexagonMap,
            toggleHexagonSelection,
            totalSelectedHexagons,
            showNeighborHexagons,
            clearPreviewHexagons,
            previewHexagons,
            setSelectedHexagons,
            hoverHexagon,
            setHoverHexagon
        } = useH3Operations({
            markers,
            initialHexagons,
            onHexagonSelect,
            autoResolution,
            isH3Enabled,
            defaultZoom,
            map: mapInstance,
            maps: null
        });

        // Initialize Leaflet map
        useEffect(() => {
            if (mapRef.current && !mapInstance) {
                // Configure Leaflet marker icons
                configureLeafletMarkerIcons(L);

                // Türkiye sınırları
                const turkeyBounds = L.latLngBounds(
                    TURKEY_BOUNDS.southWest,
                    TURKEY_BOUNDS.northEast
                );

                const map = L.map(mapRef.current, {
                    center: [defaultCenter.lat, defaultCenter.lng],
                    zoom: defaultZoom,
                    zoomControl: true,
                    doubleClickZoom: false,
                    dragging: true,
                    scrollWheelZoom: true,
                    maxBounds: turkeyBounds,
                    maxBoundsViscosity: 1.0,
                    minZoom: 7,
                    maxZoom: 22
                });

                setMapInstance(map);

                // Add tile layer with fallback
                tileLayerRef.current = addTileLayerWithFallback(L, map, getTileLayerPreference());

                // User interaction listeners to disable auto-fitting
                const handleUserInteraction = () => {
                    setHasAutoFitted(true);
                };

                map.on("zoomstart", handleUserInteraction);
                map.on("dragstart", handleUserInteraction);

                return () => {
                    if (map) {
                        map.off("zoomstart", handleUserInteraction);
                        map.off("dragstart", handleUserInteraction);
                        map.remove();
                    }
                };
            }
        }, []);

        // Handle city geocoding
        useEffect(() => {
            if (!mapInstance || !initialCity) return;

            // For Turkey cities, use approximate coordinates
            const cityCoordinates = {
                istanbul: [41.0082, 28.9784],
                ankara: [39.9334, 32.8597],
                izmir: [38.4237, 27.1428]
            };

            const cityKey = initialCity.toLowerCase();
            if (cityCoordinates[cityKey]) {
                mapInstance.setView(cityCoordinates[cityKey], 11);
            }
        }, [mapInstance, initialCity]);

        // Update marker positions when map moves or zooms
        const updateMarkerPositions = useCallback(() => {
            if (!mapInstance) return;

            const positions = {};
            markers.forEach((marker, index) => {
                if (marker?.location?.latitude && marker?.location?.longitude) {
                    const point = mapInstance.latLngToContainerPoint([
                        marker.location.latitude,
                        marker.location.longitude
                    ]);
                    positions[marker?._id || index] = {
                        x: point.x,
                        y: point.y,
                        lat: marker.location.latitude,
                        lng: marker.location.longitude
                    };
                }
            });
            setMarkerPositions(positions);
        }, [mapInstance, JSON.stringify(markers)]);

        // Initial marker positioning and fit bounds
        useEffect(() => {
            if (!mapInstance) return;

            updateMarkerPositions();

            // Fit bounds only on first marker load or when explicitly needed
            if (markers.length > 0 && !hasAutoFitted) {
                const bounds = markers
                    .filter(m => m?.location?.latitude && m?.location?.longitude)
                    .map(m => [m.location.latitude, m.location.longitude]);

                if (bounds.length > 0) {
                    mapInstance.fitBounds(bounds, {padding: [50, 50]});
                    setHasAutoFitted(true);
                }
            }
        }, [mapInstance, JSON.stringify(markers), hasAutoFitted]);

        // Update marker positions on map move/zoom
        useEffect(() => {
            if (!mapInstance) return;

            mapInstance.on("move", updateMarkerPositions);
            mapInstance.on("zoom", updateMarkerPositions);

            return () => {
                mapInstance.off("move", updateMarkerPositions);
                mapInstance.off("zoom", updateMarkerPositions);
            };
        }, [mapInstance, updateMarkerPositions]);

        // Handle mouse move for hexagon hover
        useEffect(() => {
            if (!mapInstance) return;

            const handleMouseMove = e => {
                if (!isH3Enabled || isPreview) {
                    setHoverHexagon(null);
                    return;
                }

                if (disabledResolutions.includes(resolution)) {
                    setHoverHexagon(null);
                    return;
                }

                const lat = e.latlng.lat;
                const lng = e.latlng.lng;
                const hexId = h3.latLngToCell(lat, lng, resolution);
                const hexagonData = initialHexagons.find(h => h.index === hexId);

                if (hexagonData?.isNeighbor) {
                    setHoverHexagon(null);
                    return;
                }

                if (!isActionsEnabled) {
                    const isHexagonInInitial = initialHexagons.some(hex => hex.index === hexId);
                    if (isHexagonInInitial) {
                        setHoverHexagon(hexId);
                    } else {
                        setHoverHexagon(null);
                    }
                } else {
                    setHoverHexagon(hexId);
                }
            };

            const handleMouseOut = () => {
                setHoverHexagon(null);
            };

            mapInstance.on("mousemove", handleMouseMove);
            mapInstance.on("mouseout", handleMouseOut);

            return () => {
                mapInstance.off("mousemove", handleMouseMove);
                mapInstance.off("mouseout", handleMouseOut);
            };
        }, [
            mapInstance,
            isH3Enabled,
            isActionsEnabled,
            resolution,
            disabledResolutions,
            setHoverHexagon,
            initialHexagons,
            isPreview
        ]);

        // Filter other hexagons
        useEffect(() => {
            const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

            if (!safeOtherHexagons.length || !initialHexagons) {
                setFilteredOtherHexagons([]);
                return;
            }

            const nonInitialHexagons = safeOtherHexagons.filter(
                otherHex => !initialHexagons.some(initialHex => initialHex.index === otherHex.index)
            );

            const uniqueIndexMap = new Map();
            nonInitialHexagons.forEach(hexagon => {
                if (!uniqueIndexMap.has(hexagon.index)) {
                    uniqueIndexMap.set(hexagon.index, hexagon);
                }
            });

            const uniqueHexagons = Array.from(uniqueIndexMap.values());
            setFilteredOtherHexagons(uniqueHexagons);
        }, [initialHexagons, JSON.stringify(otherHexagons)]);

        useImperativeHandle(ref, () => ({
            panToCity: async cityName => {
                if (!mapInstance) {
                    return;
                }

                const cityCoordinates = {
                    istanbul: [41.0082, 28.9784],
                    ankara: [39.9334, 32.8597],
                    izmir: [38.4237, 27.1428]
                };

                const cityKey = cityName.toLowerCase();
                if (cityCoordinates[cityKey]) {
                    mapInstance.setView(cityCoordinates[cityKey], 11);
                }
            }
        }));

        const handleHexagonClick = useCallback(
            hexagonIndex => {
                if (!mapInstance) return;
                const [lat, lng] = h3.cellToLatLng(hexagonIndex);
                mapInstance.setView([lat, lng], 12);
            },
            [mapInstance]
        );

        const handleMarkerFocus = useCallback(
            marker => {
                if (!mapInstance || !marker?.location?.latitude || !marker?.location?.longitude)
                    return;

                mapInstance.setView([marker.location.latitude, marker.location.longitude], 15);
            },
            [mapInstance]
        );

        const handleH3Selection = useCallback(
            (lat, lng) => {
                if (!isH3Enabled || !isActionsEnabled) {
                    return;
                }

                if (disabledResolutions.includes(resolution)) {
                    message.warning(`${resolution}. seviyede seçim yapılamaz.`);
                    return;
                }

                const hexId = h3.latLngToCell(lat, lng, resolution);

                const isAlreadySelected = selectedHexagons[resolution]?.has(hexId);

                toggleHexagonSelection(lat, lng);

                const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

                if (isAlreadySelected) {
                    const originalHexagon = safeOtherHexagons.find(hex => hex.index === hexId);
                    if (originalHexagon) {
                        const hexagonWithProperties = {
                            ...originalHexagon,
                            zIndex: originalHexagon.zIndex || H3MapConfig.hexagons.OTHER.zIndex
                        };
                        setFilteredOtherHexagons(prev => [...prev, hexagonWithProperties]);
                    }
                } else {
                    setFilteredOtherHexagons(prev => prev.filter(hex => hex.index !== hexId));
                }

                if (isAlreadySelected) {
                    clearPreviewHexagons();
                } else {
                    showNeighborHexagons(hexId);
                }
            },
            [
                isH3Enabled,
                isActionsEnabled,
                disabledResolutions,
                resolution,
                toggleHexagonSelection,
                selectedHexagons,
                showNeighborHexagons,
                clearPreviewHexagons,
                otherHexagons
            ]
        );

        const handleHexagonDelete = useCallback(
            (hex, resolution) => {
                setSelectedHexagons(prevSelected => {
                    const newState = {...prevSelected};
                    const currentResolutionSet = new Set(prevSelected[resolution] || []);

                    currentResolutionSet.delete(hex);

                    if (previewHexagons.length > 0) {
                        previewHexagons.forEach(hexId => {
                            currentResolutionSet.delete(hexId);
                        });
                    }

                    if (currentResolutionSet.size === 0) {
                        delete newState[resolution];
                    } else {
                        newState[resolution] = currentResolutionSet;
                    }

                    return newState;
                });

                const safeOtherHexagons = Array.isArray(otherHexagons) ? otherHexagons : [];

                const isHexagonInOriginalOtherHexagons = safeOtherHexagons.some(
                    item => item.index === hex
                );
                if (isHexagonInOriginalOtherHexagons) {
                    const hexagonToAdd = safeOtherHexagons.find(item => item.index === hex);
                    const hexagonWithProperties = {
                        ...hexagonToAdd,
                        zIndex: hexagonToAdd.zIndex || H3MapConfig.hexagons.OTHER.zIndex
                    };
                    setFilteredOtherHexagons(prev => [...prev, hexagonWithProperties]);
                }

                clearPreviewHexagons();
            },
            [clearPreviewHexagons, previewHexagons, otherHexagons]
        );

        function handleMapClick(event) {
            const {lat, lng} = event.latlng;
            handleH3Selection(lat, lng);
        }

        // Listen for map clicks
        useEffect(() => {
            if (!mapInstance) return;

            mapInstance.on("click", handleMapClick);

            return () => {
                mapInstance.off("click", handleMapClick);
            };
        }, [mapInstance, handleH3Selection]);

        return (
            <>
                <GlobalStyle />
                <MapContainer style={style}>
                    <div
                        ref={mapRef}
                        style={{
                            width: "100%",
                            height: "100%",
                            zIndex: 1
                        }}
                    />

                    {/* Render markers as React components */}
                    {markers.map((marker, index) => {
                        const markerId = marker?._id || index;
                        const position = markerPositions[markerId];

                        if (!position) return null;

                        return (
                            <Marker
                                key={`marker-${markerId}`}
                                size={20}
                                lat={position.lat}
                                lng={position.lng}
                                icon={markerIcon}
                                text={marker?.name}
                                isH3Enabled={isH3Enabled}
                                color={markerColor}
                                $hover={hoveredMarker === markerId}
                                data-testid="mapMarker"
                                style={{
                                    left: `${position.x}px`,
                                    top: `${position.y}px`
                                }}
                                onClick={() => {
                                    onMarkerClick?.(marker);
                                    if (marker?.location?.latitude && marker?.location?.longitude) {
                                        handleH3Selection(
                                            marker.location.latitude,
                                            marker.location.longitude
                                        );
                                    }
                                }}
                                onMouseEnter={() => setHoveredMarker(markerId)}
                                onMouseLeave={() => setHoveredMarker(null)}
                            >
                                {markerIcon}
                            </Marker>
                        );
                    })}

                    {/* Render hexagons */}
                    {selectedHexagons &&
                        mapInstance &&
                        Object.entries(selectedHexagons).map(([resolution, hexagons]) =>
                            Array.from(hexagons).map(hexId => {
                                const hexagonData = initialHexagons.find(h => h.index === hexId);
                                const hexResolution = h3.getResolution(hexId);
                                const calculatedZIndex = 50 + hexResolution * 10;

                                return (
                                    <Hexagon
                                        key={hexId}
                                        hexagon={hexId}
                                        hoverHexagon={hoverHexagon}
                                        isSelected={true}
                                        isPreview={false}
                                        color={
                                            hexagonData?.color ||
                                            H3MapConfig.hexagons.SELECTED.color
                                        }
                                        setHoverHexagon={setHoverHexagon}
                                        opacity={
                                            hexagonData?.isNeighbor
                                                ? 0.5
                                                : H3MapConfig.hexagons.SELECTED.opacity
                                        }
                                        map={mapInstance}
                                        showTooltip={showHexagonTooltip}
                                        zIndex={calculatedZIndex}
                                        tooltip={hexagonData?.tooltip}
                                    />
                                );
                            })
                        )}

                    {/* Preview hexagons */}
                    {Array.isArray(previewHexagons) &&
                        mapInstance &&
                        previewHexagons.map(hexId => (
                            <Hexagon
                                key={hexId}
                                hexagon={hexId}
                                isPreview={true}
                                color={H3MapConfig.hexagons.PREVIEW.color}
                                opacity={H3MapConfig.hexagons.PREVIEW.opacity}
                                map={mapInstance}
                                hoverHexagon={hoverHexagon}
                                setHoverHexagon={setHoverHexagon}
                                zIndex={H3MapConfig.hexagons.PREVIEW.zIndex}
                                showTooltip={showHexagonTooltip}
                            />
                        ))}

                    {/* Hover hexagon */}
                    {hoverHexagon && mapInstance && (
                        <Hexagon
                            key={`hover-${hoverHexagon}`}
                            hexagon={hoverHexagon}
                            isPreview={false}
                            isSelected={true}
                            color={H3MapConfig.hexagons.HOVER.color}
                            opacity={H3MapConfig.hexagons.HOVER.opacity}
                            map={mapInstance}
                            hoverHexagon={hoverHexagon}
                            setHoverHexagon={setHoverHexagon}
                            zIndex={H3MapConfig.hexagons.HOVER.zIndex}
                            showTooltip={showHexagonTooltip}
                        />
                    )}

                    {/* Other hexagons */}
                    {mapInstance &&
                        showOtherHexagons &&
                        filteredOtherHexagons &&
                        filteredOtherHexagons.length > 0 &&
                        filteredOtherHexagons.map((hexagon, idx) => {
                            const hexResolution = h3.getResolution(hexagon.index);
                            const calculatedZIndex = 50 + hexResolution * 10;

                            return (
                                <Hexagon
                                    key={`other-${hexagon.index}-${idx}`}
                                    hexagon={hexagon.index}
                                    isSelected={false}
                                    isPreview={false}
                                    map={mapInstance}
                                    hoverHexagon={hoverHexagon}
                                    color={hexagon.color || H3MapConfig.hexagons.OTHER.color}
                                    opacity={hexagon.opacity || H3MapConfig.hexagons.OTHER.opacity}
                                    showTooltip={showHexagonTooltip}
                                    setHoverHexagon={setHoverHexagon}
                                    zIndex={calculatedZIndex}
                                    tooltip={hexagon.tooltip}
                                />
                            );
                        })}

                    {/* Tile Layer Control - Sadece showTileLayerControl true ise göster */}
                    {mapInstance && showTileLayerControl && (
                        <div
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                zIndex: 1000
                            }}
                        >
                            <TileLayerControl
                                mapInstance={mapInstance}
                                tileLayerRef={tileLayerRef}
                            />
                        </div>
                    )}

                    {/* Other Hexagons Panel with Tile Layer Control */}
                    {isH3Enabled && showOtherHexagonsPanel && (
                        <OtherHexagonsPanel
                            showOtherHexagons={showOtherHexagons}
                            setShowOtherHexagons={setShowOtherHexagons}
                            mapInstance={mapInstance}
                            tileLayerRef={tileLayerRef}
                        />
                    )}

                    {/* Tools Panel */}
                    {isH3Enabled && showToolsPanel && (
                        <H3MapTools
                            resolution={resolution}
                            setResolution={setResolution}
                            resolutionOptions={resolutionOptions}
                            totalHexagons={totalSelectedHexagons}
                            hexagonsByResolution={selectedHexagons}
                            markerH3Indexes={markerToHexagonMap}
                            handleListItemClick={handleHexagonClick}
                            handleMarkerZoom={handleMarkerFocus}
                            markerColor={markerColor}
                            isVisible={isToolsPanelVisible}
                            setIsVisible={setIsToolsPanelVisible}
                            disabledResolutions={disabledResolutions}
                            hoverHexagon={hoverHexagon}
                            setHoverHexagon={setHoverHexagon}
                            onHexagonDelete={handleHexagonDelete}
                            isActionsEnabled={isActionsEnabled}
                            onlyH3Resolutions={onlyH3Resolutions}
                            showOtherHexagons={showOtherHexagons}
                            setShowOtherHexagons={setShowOtherHexagons}
                            otherHexagons={filteredOtherHexagons}
                        />
                    )}
                </MapContainer>
            </>
        );
    }
);
